import { useDocumentAssets } from "@/composables/documentAssets";
import { useBookingsStore } from "@/store/bookings";
import { useUserStore } from "@/store/user";
import {
  MarketplaceApi,
  useApiListQuery,
  useBookingorderMemos,
  useBookingorders,
  type LogisticStatus,
} from "@xs2event/js-lib/api";
import { camelToSnake, defineContext, t, useFilters, utcToApiDate } from "@xs2event/js-lib/util";
import { computed, ref } from "vue";
import { useRoute } from "vue-router";

const [provideBookingContext, useBookingContext] = defineContext(() => {
  const bookingsStore = useBookingsStore();

  const filters = useFilters({
    search: { default: "" },
    date: {
      default: undefined as [string, string] | string | undefined,
      serialize: (x) => {
        if (typeof x != "string") return undefined;

        if (x.includes(",")) {
          return x.split(",").join(",");
        }
        return x;
      },
      deserialize: (x) => {
        if (typeof x == "string" && x.includes(",")) {
          return x.split(",");
        }
        return x?.split(",");
      },
    },
    eventId: { default: undefined as string | undefined },
    logisticStatus: { default: undefined as LogisticStatus | undefined },
    futureEvents: { default: 0 as 0 | 1 },
    page: { default: 1 },
    pageSize: { default: 25 },
    sorting: { default: "created:desc" },
  });

  const selectedBookingorder = ref<Bookingorder>();

  const openBookingorder = (id: string) => {
    bookingsStore.fetchBookingOrder(id);
    bookingsStore.getMemosByBookingorderIds([id]);
  };

  const openBookingorderMemos = (id: string) => {
    openBookingorder(id);
    bookingsStore.openTab(4);
  };

  return { filters, openBookingorder, openBookingorderMemos, selectedBookingorder };
});

export type Bookingorder = ReturnType<typeof useOverviewBookingorders>["data"]["value"][number];

export const useOverviewBookingorders = () => {
  const { filters } = useBookingContext();

  const route = useRoute();

  const userStore = useUserStore();

  const { search, eventId, date, logisticStatus, page, pageSize, sorting } = filters;

  const bookingordersQuery = useBookingorders(
    computed(() => ({
      query: search.value?.trim() ? `ilike:${search.value.trim()}` : undefined,
      eventId: eventId.value,
      eventStartdate: date.value?.[0] && `ge:${utcToApiDate(date.value[0])}`,
      eventStopdate: date.value?.[1] && `le:${utcToApiDate(date.value[1])}`,
      logisticStatus: logisticStatus.value,
    })),
    {
      pageSize: pageSize,
      page: page,
      sorting: computed(() => camelToSnake(sorting.value || "created:desc")),

      enabled: computed(() => (userStore.isAdmin && !!Object.keys(route.query).length) || !userStore.isAdmin),
    }
  );

  // Fetch Assets for each booking order
  const documentAssetsQuery = useDocumentAssets(
    computed(() => ({
      relatedType: "bookingorder",
      bookingorderId: `in:${Array.from(bookingordersQuery.data.value || [])
        .map((bo) => bo.bookingorderId)
        .filter(Boolean)
        .join(",")}`,
    })),
    { enabled: computed(() => !!bookingordersQuery.data.value.length) }
  );

  const bookingorderMemosQuery = useBookingorderMemos(
    computed(() => Array.from(new Set(bookingordersQuery.data.value?.map((bo) => bo.bookingorderId!))))
  );

  const marketplaceApi = new MarketplaceApi();

  const relistedTicketsQuery = useApiListQuery(
    marketplaceApi,
    "marktplaceticketsGet",
    {
      isRelistedTicket: "true",
      ticketStatus: "in:[available,disabled]",
      supplierId: userStore.supplier_id,
    },
    { pageSize: "all", enabled: computed(() => userStore.isAdmin || userStore.isSupplier) }
  );

  const isLoading = computed(
    () =>
      documentAssetsQuery.isFetching.value ||
      bookingordersQuery.isFetching.value ||
      bookingorderMemosQuery.isFetching.value ||
      relistedTicketsQuery.isFetching.value
  );

  const data = computed(() => {
    if (isLoading.value) return [];

    return bookingordersQuery.data.value?.map((bookingorder) => {
      const documentAssets =
        documentAssetsQuery.data.value?.filter((asset) => asset.relatedId === bookingorder.bookingorderId) || [];

      const memos =
        bookingorderMemosQuery.data.value?.filter((memo) => memo.bookingorderId === bookingorder.bookingorderId) || [];

      const listing = relistedTicketsQuery.data.value?.find((ticket) =>
        ticket.externalTicketId?.includes(bookingorder.bookingorderId!)
      );

      return {
        ...bookingorder,
        documentAssets,
        memos,
        quantity: bookingorder.items?.reduce((sum, item) => sum + (item.quantity || 0), 0),
        listing: listing && listing.stock ? listing : undefined,
        ticketName: bookingorder.items?.length
          ? bookingorder.items?.map((item) => item.ticketName).join(", ")
          : t("Various"),
      };
    });
  });

  return { data, isLoading, pages: bookingordersQuery.pages };
};

export { provideBookingContext, useBookingContext };
